/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

/**
 * settings-store.js — App-wide preferences. A single JSON document under
 * userData; `get()` returns the stored values layered over the frozen
 * DEFAULTS, `set(patch)` shallow-merges a patch and persists atomically.
 * Object-valued keys (like `viewport`) are replaced whole by a patch, not
 * deep-merged — callers write the full object.
 */
"use strict";

const fs = require("fs");
const path = require("path");
const io = require("./io");

const DEFAULTS = Object.freeze({
  // ── Desk viewport (Feature 10) ────────────────────────────────────────────
  // The camera restored at boot: world-unit center + zoom factor.
  viewport: Object.freeze({ cx: 0, cy: 0, zoom: 1 }),

  // ── Window bounds ─────────────────────────────────────────────────────────
  // {x,y,width,height} persisted by window-state.js; null → centred default.
  windowBounds: null,

  // ── Parts palette (Feature 40) ────────────────────────────────────────────
  // Whether the left palette panel is open (the palette flap / chevron and ⌘P
  // toggle it). Open by default — the tray is how parts reach the desk, so a
  // first launch shows it rather than hiding it behind the drawer pull.
  // Which groups are collapsed is NOT stored: the palette opens with every
  // group shut on every launch, and what the user opens lasts the session.
  paletteOpen: true,

  // How wide that tray is, in CSS px — written by its draggable right edge
  // (the renderer clamps it to [180, half the window], and the panel's
  // `max-width: 50vw` re-caps a width saved on a wider display). Unlike the
  // collapse state this IS remembered: it is a working preference about how
  // much of the desk a permanent left column may take, not a transient one.
  paletteWidth: 232,

  // Whether opening a section of that tray closes every section that is not
  // on the way to it (Settings ▸ Appearance ▸ "Auto-close tray folders"), so
  // only the shelf being worked in stays open. Off by default: the tray has
  // always left open whatever the user opened. Applied live, and it closes
  // nothing by itself — it acts on the NEXT section opened.
  paletteAutoClose: false,

  // Whether the toolbar offers the 3D view at all (Settings ▸ Appearance ▸
  // "3D enabled"). Off by default: the segment is hidden until it is switched
  // on, and switching it off while the 3D view is showing returns to the
  // breadboard. The view itself is still a toolbar toggle — this only decides
  // whether that toggle is there.
  view3dEnabled: false,

  // ── Chip designer window ──────────────────────────────────────────────────
  // Where its two dividers were left, in CSS px: the one between the package
  // (left) and the code (right), and the one under the generated module
  // header. Written by the window when a divider is let go; null — the
  // default, and what a double-click on a divider goes back to — leaves the
  // window's own layout (the package 420 px wide, the header as tall as it
  // needs). The window clamps both to what fits.
  chipDesignerLeftWidth: null,
  chipDesignerHeaderHeight: null,

  // ── CPU monitor window ────────────────────────────────────────────────────
  // Whether the bytes typed into a ROM through the monitor during a run are
  // written to the ROM's image when the run stops (as the memory inspector's
  // Save would), or dropped with the run. The window's own checkbox; written
  // by the main renderer's CpuMonitorBridge.
  cpuMonitorKeepEdits: false,

  // ── Pin-assignments window (Feature 100) ──────────────────────────────────
  // Whether a chip's pin-out window floats above the main app. A de-facto
  // global preference: the window's right-click menu toggles it, every open
  // pinout window follows, and a future settings dialog binds to it.
  pinoutFloat: true,

  // ── Settings dialog ────────────────────────────────────────────────────────
  // Light / dark appearance: "system" (follow the OS, the default), "light",
  // or "dark". Main applies it as Electron's `nativeTheme.themeSource`, which
  // is what every renderer's `prefers-color-scheme` — and the native menus and
  // dialogs — then follow, so there is ONE switch for the whole app.
  theme: "system",

  // The UI language: "system" (follow the OS locale, the default) or a language
  // subtag a catalog ships for ("en", "de", "es", "fr", "it", "ja", "zh" — see
  // app/i18n.js's LOCALES). Main resolves it, because it owns both the OS locale
  // and the files the catalogs live in; a language with no catalog falls back to
  // English rather than failing. Like `theme` this is main's to act on, so
  // settings:set re-resolves the catalog and rebuilds the native menu when it
  // changes; the renderer relabels its own chrome off `chiphippo:locale-changed`.
  locale: "system",

  // The size, in CSS px, of Chip Hippo's OWN text — its toolbars, panels,
  // menus and dialogs, in every window at once (Settings ▸ Appearance ▸
  // "Editor font size", or ⌥⌘= / ⌥⌘− / ⌥⌘0). One of six steps — 11, 12, 13,
  // 14, 16, 18; web/scripts/font-scale.js owns the list and the renderer
  // normalizes anything else, since this store has no validation layer by
  // design. 13 is the shipped value and reproduces the app's original type
  // scale exactly.
  //
  // A NUMBER, not a multiplier: theme.css derives its whole type scale
  // additively off this one px value, so the setting IS the base of the scale.
  // Like `theme` and `locale` it is main's to act on — but it is the only one
  // of the three with no native mechanism to ride (there is no font-size
  // equivalent of `nativeTheme.themeSource`), so settings:set fans it out to
  // every window by hand.
  //
  // What it does NOT touch is the desk: chip labels, board markings, schematic
  // symbols and annotations are drawn in world units and follow the desk
  // camera, because they are printed on the circuit rather than being chrome.
  fontSize: 13,

  // ── Auto-update (Feature 280; Settings ▸ About) ───────────────────────────
  // Whether the app checks the release feed for a newer version shortly after
  // launch. OFF by default, deliberately: an update check is an outbound call,
  // and Chip Hippo makes none of those until it is asked to — the same stance
  // the AI builder and the datasheet download take. Turning it off does not
  // take the feature away; Settings ▸ About and Help ▸ Check for Updates…
  // still check on demand. Ignored entirely in a Mac App Store build, where
  // the store does the updating (see app/store-build.js).
  autoUpdateCheck: false,

  // The selection-border colour (`#rrggbb`), or null to use the theme accent.
  // Applied by the renderer as the `--color-selection` custom property.
  // Defaults to the house orange (the `--color-wire-orange` value): the theme
  // accent is monochrome, so an outline drawn in it reads as one more grey
  // line on the desk.
  selectionColor: "#d0804a",

  // The color a newly placed LED gets (Settings ▸ Appearance ▸ "Default LED
  // color") — one of catalog/parts.js's LED_COLOR_OPTIONS. Only the default
  // for NEW LEDs; an already-placed LED's color is changed through its own
  // Properties dialog (right-click ▸ Properties…).
  defaultLedColor: "red",

  // ── Desk drawing ──────────────────────────────────────────────────────────
  // Whether wires are faded back to a short stub at each end (the toolbar's
  // "Fade wires" toggle, or H) so a heavily wired board stays readable. A
  // selected wire is always drawn whole.
  wiresFaded: false,

  // How a NEWLY laid wire is drawn (Settings ▸ Appearance ▸ "Wire layout") —
  // "direct" (the sagging hole-to-hole curve, the default) or "routed" (a
  // straight run the user bends by dragging waypoints into it). Only the
  // default for new wires: an existing one's layout is changed through its own
  // Properties dialog, and the AI builder ignores this setting outright — a
  // generated circuit is always direct.
  defaultWireLayout: "direct",

  // The look a multimeter taken out wears — "volthippo" (the default) or
  // "bm235" (the EEVBlog look). Not in Settings: it is the last look picked
  // in a meter's own Properties ▸ Look, and changes no meter already out.
  meterLook: "volthippo",

  // Whether the note a capacitor shows as it lands on the board (what a
  // capacitor does and does not do in a logic sim) has been dismissed for
  // good with its "Don't show again". Written only by that button.
  capacitorNoteDismissed: false,

  // ── What's-new notices (the banner over the desk) ─────────────────────────
  // The version this machine FIRST ran, stamped once by main at launch
  // (`firstRunPatch`): the running version on a fresh install, or "legacy"
  // when an earlier install left settings or saves behind — some build from
  // before the stamp existed (≤ 1.4.2). Main-owned: it is what decides who
  // counts as an upgrader, so the renderer only ever reads it. null until main
  // has written it, and the banner shows nothing while it is.
  firstRunVersion: null,

  // The ids of the notices the user has closed with the banner's ✕
  // (web/scripts/model/notices.js). Closed is closed for good: a later version
  // that needs to say something again ships a notice with a NEW id.
  dismissedNotices: Object.freeze([]),

  // ── Panels (Build guide / Logic analyzer) ──────────────────────────────────
  // Whether the right-docked build guide is shown.
  guideOpen: false,

  // Whether the bottom-docked logic analyzer is shown, and its height in CSS
  // px. The panel's draggable top edge writes the height (the renderer clamps
  // it to half the window); the toolbar / close button writes the open flag.
  scopeOpen: false,
  scopeHeight: 280,
  // How the analyzer lays its trace across: "timing" (x is simulated time)
  // or "transitions" (x advances by change). The header's picker writes it;
  // the renderer coerces anything else to "timing" (model/scope-axis.js).
  scopeAxis: "timing",

  // The bottom-docked AI builder panel — same two keys, same reasons.
  aiOpen: false,
  aiHeight: 280,

  // The prompts the user has asked the AI builder for, newest first, capped at
  // MAX_HISTORY (web/scripts/ai/prompt-history.js). Deliberately here and not
  // in the project file: what you have asked for before is yours, not the
  // design's, so arrowing back through it works the same in every project —
  // including a brand-new one, where a per-project list would always be empty.
  aiHistory: Object.freeze([]),

  // ── Data sheets ────────────────────────────────────────────────────────────
  // An external directory of manufacturer datasheet PDFs. When it is set and a
  // `<dir>/<partId>.pdf` exists, the pin-assignments window shows a button that
  // opens that PDF natively. null → no directory (the default).
  datasheetDir: null,

  // Which logic family the parts tray shows (Feature 400; Settings ▸ Data
  // Sheets ▸ Chip family): "74LS" (the default, so the tray is what it always
  // was), "CD4000", or "combined" — both, each in a folder of its own. App-wide
  // and not per project, deliberately: it is a view filter, and a project flag
  // is an unsaved change. The renderer coerces anything else to the default
  // (catalog/families.js `normalizeFamilyMode`).
  logicFamily: "74LS",

  // ── Spice Lite (Settings ▸ Spice Lite; features/spice-lite.md) ────────
  // The second, more electrical simulation engine: whether Run uses it (on
  // by default since 2026-10-23 — a user who has stored `enabled: false`
  // keeps the logic engine), each logic family's OVERRIDES of its datasheet defaults (only what the user
  // changed; Reset deletes a family's entry), and whether a switch's
  // contacts bounce (`bounce`). (A `gapPercent` stored before 2026-10-23,
  // when the settle gap was fixed at 1 %, is ignored.) App-wide like
  // `logicFamily` —
  // a way of simulating, not part of a design. Stored unvalidated, as `ai`
  // is; the renderer coerces it (web/scripts/sim/spice/config.js). Replaced
  // whole by a patch, like every object-valued key.
  spiceLite: Object.freeze({
    enabled: true,
    families: Object.freeze({}),
    bounce: false,
  }),

  // ── AI circuit builder (Feature 260) ───────────────────────────────────────
  // The NON-SECRET half of the user's own AI connection: which provider, where
  // it lives, and which model. The API key is deliberately absent — it lives
  // OS-encrypted in store/credential-store.js, because this file is plaintext
  // and is handed back to the renderer in full on every `settings:get`.
  // An object-valued key is replaced whole, so a caller sends the whole thing.
  ai: {
    provider: "anthropic", // "anthropic" | "openai-compat"
    baseUrl: "", // blank → the provider's own default
    model: "", // blank → the provider's own default
  },

  // ── Arduino serial integration (Settings ▸ Serial I/O) ─────────────────────
  // The machine's NAMED CONNECTIONS: `{id, name, port, baud, dataBits, parity,
  // stopBits, flowControl, language, needsConfig?}` each (see the renderer's
  // model/serial-connections.js). Here rather than in the project because a
  // PORT is a fact about this computer; a project carries the rest of each
  // connection it uses, and one it brings that this machine lacks is added
  // here flagged `needsConfig`. Replaced whole by a patch, like every
  // object-valued key. It is also the ALLOWLIST `serial:open` reads: the
  // renderer names connection ids, and only main turns one into a device.
  serialConnections: Object.freeze([]),

  // Each CONNECTION WINDOW's remembered state, keyed by the connection's ID
  // (docs/chiphippo-connection-window.md): where it was and how big
  // (`bounds`) and how it was showing its stream (`log` / `data` /
  // `protocol` filters, `timestamps`). Written by main (ipc/serial.js) as the
  // window moves and as its toggles change. Kept only for connections that
  // exist (and the built-in Mock): writing `serialConnections` without one
  // drops its entry, so a rename keeps a window where it was and a deletion
  // takes its window's state with it.
  connectionWindows: Object.freeze({}),

  // Where Generate's Save As… last put each generated file, so the next Save
  // As of it opens there: connection ID → design scope → file name → path
  // (app/serial/saved-files.js). Written by main (ipc/serial.js), only ever
  // with a path a Save panel returned; pruned like `connectionWindows` when
  // a connection is deleted.
  codegenSaves: Object.freeze({}),

  // ── Recent projects ────────────────────────────────────────────────────────
  // The last 10 PROJECT files saved or opened, most recent first. Main owns
  // the list (store/recent-files.js does the arithmetic) and it does two jobs:
  // it is the Projects ▸ Open Recent menu, and it is the ALLOWLIST that
  // project:open-recent checks a path against, so the renderer can never ask
  // main to read an arbitrary file. It is also where startup looks when the
  // app's saves folder holds no unsaved (default) project — the head of this
  // list is then the project the session opens with.
  //
  // There is deliberately no equivalent for DESKTOP files: a desktop is
  // reached through the project that owns it, never from a recent list.
  recentProjects: Object.freeze([]),
});

class SettingsStore {
  /**
   * @param {string} dataDir - the app's userData directory.
   */
  constructor(dataDir) {
    this._file = path.join(dataDir, "settings.json");
  }

  /** Whether the settings file is on disk at all — an earlier launch's mark. */
  exists() {
    return fs.existsSync(this._file);
  }

  _read() {
    const doc = io.readJSON(this._file);
    return doc && typeof doc === "object" && !Array.isArray(doc) ? doc : {};
  }

  /** Current settings: stored values layered over the defaults. */
  get() {
    return { ...DEFAULTS, ...this._read() };
  }

  /**
   * Shallow-merge `patch` into the stored settings and persist. Returns the
   * full merged settings (defaults + stored).
   * @param {object} patch
   * @returns {object}
   */
  set(patch) {
    if (!patch || typeof patch !== "object" || Array.isArray(patch)) {
      const err = new Error("settings patch must be an object");
      err.code = "INVALID_ARG";
      throw err;
    }
    const next = { ...this._read(), ...patch };
    io.writeJSON(this._file, next);
    return { ...DEFAULTS, ...next };
  }
}

/**
 * Keys MAIN keeps for itself. The renderer never writes one, and the first is
 * load-bearing: `recentProjects` IS the allowlist Open Recent is checked
 * against, so a renderer that could write it could have main adopt — and later
 * save over — any path on disk, every other path gate notwithstanding.
 */
const MAIN_OWNED = Object.freeze([
  "recentProjects",
  "windowBounds",
  "pinoutFloat",
  "connectionWindows",
  "codegenSaves",
  "firstRunVersion",
]);

/**
 * The `firstRunVersion` stamp a launch should write, or null when there is
 * nothing to write. Decided ONCE: a stored stamp is never touched again, so a
 * user stays an upgrader (or not) for every launch after the one that said so.
 *
 * @param {object} stored - the settings as stored (not layered on DEFAULTS).
 * @param {{priorInstall: boolean, version: string}} opts - `priorInstall`:
 *   an earlier launch left settings or saves behind; `version`: this build's.
 * @returns {{firstRunVersion: string}|null}
 */
function firstRunPatch(stored, { priorInstall, version }) {
  if (typeof stored?.firstRunVersion === "string") return null;
  return { firstRunVersion: priorInstall ? "legacy" : version };
}

/**
 * The part of a `settings:set` patch the renderer may write.
 *
 * Main-owned keys are dropped, and `datasheetDir` — a folder main later hands
 * to `shell.openPath` — survives only as null, or a folder `mayUseDir` vouches
 * for (one a dialog established, or the app's own download folder).
 *
 * @param {object} patch
 * @param {{mayUseDir: (dir: string) => boolean}} opts
 * @returns {object} a new object; the input is never mutated.
 */
function rendererPatch(patch, { mayUseDir }) {
  if (!patch || typeof patch !== "object" || Array.isArray(patch)) return patch;
  const out = {};
  for (const [key, value] of Object.entries(patch)) {
    if (MAIN_OWNED.includes(key)) continue;
    if (key === "datasheetDir") {
      const ok =
        value == null ||
        value === "" ||
        (typeof value === "string" && mayUseDir(value));
      if (!ok) continue;
    }
    out[key] = value;
  }
  return out;
}

module.exports = {
  SettingsStore,
  DEFAULTS,
  MAIN_OWNED,
  rendererPatch,
  firstRunPatch,
};
